import { getUserDisplayName } from '../../../apps/professional-app/fa/common/aifeatures/utils'

import { SelectOption } from '../../../components/SelectDropdown/SelectDropdown'

import { DocumentCreate, DocumentFile, DocumentFileType } from '../../../models/Document'
import { DocumentBatchCreate } from '../../../models/DocumentBatch'
import { User } from '../../../models/User'

import { type SuccessfulDocumentUploadQueueItem } from '../hooks'

export const MAX_DOCUMENT_BATCH_FILES = 10

export const MAX_DOCUMENT_FILE_SIZE_BYTES = 20 * 1024 * 1024

export enum DocumentUploadSource {
  ZOCKS = 'zocks',
  INTEGRATION = 'integration',
}

const CONTENT_TYPE_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  txt: 'text/plain',
  csv: 'text/csv',
}

const inferContentType = (name: string): string => {
  const extension = name.split('.').pop()?.toLowerCase()
  return (extension && CONTENT_TYPE_BY_EXTENSION[extension]) || 'application/octet-stream'
}

const successfulItemToDocumentFile = (item: SuccessfulDocumentUploadQueueItem): DocumentFile => {
  if (item.source === DocumentUploadSource.INTEGRATION) {
    const { serviceId, fileId, name, size } = item
    return {
      type: DocumentFileType.INTEGRATION,
      fileId,
      connector: serviceId,
      size,
      name,
      contentType: inferContentType(name),
    }
  }

  const { fileInfo, hash } = item
  const { fileName, fileId, fileSize, contentType } = fileInfo

  return {
    type: DocumentFileType.ZOCKS,
    fileId,
    size: fileSize,
    name: fileName,
    contentType,
    ...(hash ? { hash } : {}),
  }
}

export const uploadQueueItemToDocumentCreate = (item: SuccessfulDocumentUploadQueueItem, contactId: string): DocumentCreate => {
  const file = successfulItemToDocumentFile(item)

  return {
    name: file.name,
    contactId,
    files: [file],
  }
}

export const uploadQueueItemsToDocumentBatchCreate = (items: SuccessfulDocumentUploadQueueItem[], contactId: string): DocumentBatchCreate => {
  return {
    documents: items.map((item) => uploadQueueItemToDocumentCreate(item, contactId)),
  }
}

export const zocksUserToSelectOption = (user: User): SelectOption => {
  const displayName = getUserDisplayName(user)
  return {
    id: user.id,
    label: `${displayName} (${user.email})`,
  }
}

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) {
    return '0 B'
  }

  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

export const hashFile = async (file: File) => {
  const buffer = await file.arrayBuffer()
  const digest = await crypto.subtle.digest('SHA-256', buffer)
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

export const isFileSizeValid = (bytes: number) => {
  return bytes > 0 && bytes <= MAX_DOCUMENT_FILE_SIZE_BYTES
}
